export const permissions = {
  userManagement: "user.manage",
  studySettings: "study.settings",
  studyDesigner: "form.manage",
  auditLogs: "audit.read",
  export: "export",
  dashboardView: "dashboard.view",
  econsentTrackerView: "econsent_tracker.view",
  econsentView: "econsent.view",
  todoView: "todo.view",
  informationSheetView: "information_sheet.view",

  viewParticipant: "participant.read",
  viewParticipantEmail: "participant.email",
  viewParticipantPhone: "participant.phone",
  recordLockUnlock: "participant.lock_unlock",
  recordArchive: "participant.archive",
  addParticipant: "participant.create",
  editParticipant: "participant.edit",
  // Randomization, rebuilt module (randomization spec 14, 21.7).
  // The two old codes below stay until the retirement release (spec 22), so a
  // study that has not migrated keeps working.
  canAllocateRandomization: "participant.randomization_allocate",
  canViewRandomization: "participant.randomization_view",

  randomizationSettingsView: "randomization.settings_view",
  randomizationSettingsManage: "randomization.settings_manage",
  randomizationSequenceGenerate: "randomization.sequence_generate",
  randomizationListView: "randomization.list_view",
  randomizationListUpload: "randomization.list_upload",
  randomizationListDownload: "randomization.list_download",
  randomizationStrataGenerate: "randomization.strata_generate",
  randomizationAllocate: "randomization.allocate",
  randomizationAllocationView: "randomization.allocation_view",
  randomizationArmView: "randomization.arm_view",
  randomizationUnblind: "randomization.unblind",
  randomizationReverse: "randomization.reverse",
  randomizationMrtManage: "randomization.mrt_manage",
  randomizationMrtView: "randomization.mrt_view",
  randomizationExport: "randomization.export",
  randomizationTestbench: "randomization.testbench",
  randomizationSeedView: "randomization.seed_view",
  randomizationStratumView: "randomization.stratum_view",
  randomizationReasonView: "randomization.reason_view",
  randomizationUnblindingView: "randomization.unblinding_view",
  randomizationUnblindingDisclosureView:
    "randomization.unblinding_disclosure_view",
  // Stratification factors (SFT-6). `config_manage` SITS ALONGSIDE
  // `settings_manage` rather than replacing it — `settings_manage` keeps its
  // row until a retirement release, and the overlap is deliberate.
  randomizationConfigManage: "randomization.config_manage",
  randomizationConfigWithdraw: "randomization.config_withdraw",
  randomizationSiteStratumAdd: "randomization.site_stratum_add",
  amendQueries: "query.manage",
  sendSurveys: "participant.send_survey",
  editSurveyResponse: "participant.edit_survey_response",
  downloadSyncData: "participant.download_sync_data",
};

export const platformAdminPermissions = {
  managePlatform: "platform.manage", //can create studies, platform users crud
  viewPlatform: "platform.view", //no create study, no platform users crud
};

interface Permission {
  id: string;
  label: string;
  code: string;
  description: string;
  isPublished: boolean;
}

interface Role {
  id: string;
  label: string;
  code: string;
  studyId: string;
  description: string | null;
  permissions: Array<{
    id: string;
    roleId: string;
    permissionId: string;
    isEnabled: boolean;
  }>;
}

export const formatRolesAndPermissions = (
  roles: Role[],
  permissions: Permission[]
): any => {
  const headers = roles?.map((role) => ({
    id: role?.id,
    label: role?.label,
    code: role?.code,
  }));

  const rows = permissions?.map((permission) => {
    const rowData: any = {
      label: permission?.label,
      code: permission?.code,
      id: permission?.id,
      description: permission?.description,
    };

    roles?.forEach((role) => {
      const permissionForRole = role?.permissions?.find(
        (p) => p.permissionId === permission?.id
      );
      rowData[role?.id] = permissionForRole
        ? permissionForRole?.isEnabled
        : false;
    });

    return rowData;
  });

  return { headers, rows };
};

export const formatFormPermissions = (
  roles: Role[],
  forms: { id: string; name: string; roleIds: any[] }[]
): any => {
  const headers = roles?.map((role) => ({
    id: role?.id,
    label: role?.label,
    code: role?.code,
  }));

  const rows = forms?.map((form) => {
    const rowData: any = {
      id: form?.id,
      name: form?.name,
    };
    headers.forEach((role) => {
      rowData[role.id] = false;
    });
    form.roleIds?.forEach((role) => {
      rowData[role] = true;
    });

    return rowData;
  });

  return { headers, rows };
};

export const permissionAccess = (
  siteId: string,
  permission: string,
  sitePermissions: any
) => {
  if (sitePermissions?.[siteId]?.permissions?.includes(permission)) {
    return true;
  }
  return false;
};

export const codeMap: any = {
  "user.manage": "User Management",
  "study.settings": "Study Settings",
  "form.manage": "Study Designer",
  "audit.read": "Audit Logs",
  export: "Export",
  "participant.read": "View Participant",
  "participant.lock_unlock": "Participant Lock and Unlock",
  "participant.archive": "Participant Archive",
  "participant.create": "Add Participant",
  "participant.edit": "Edit Participant",
  "participant.randomization_allocate": "Allocate Randomization",
  "participant.randomization_view": "View Randomization",

  // Randomization, rebuilt module. Labels mirror the seeder in
  // database/seeders/20260908120001-permission.randomization.create.js so the
  // screen and the API agree; the screen itself needs no change, it renders
  // whatever the permission list returns (spec 21.7).
  "randomization.settings_view": "View Randomization Settings",
  "randomization.settings_manage": "Manage Randomization Settings",
  "randomization.sequence_generate": "Generate Randomization Sequence",
  "randomization.list_view": "View Randomization List",
  "randomization.list_upload": "Upload Randomization List",
  "randomization.list_download": "Download Randomization List",
  "randomization.strata_generate": "Generate Missing Strata",
  "randomization.allocate": "Randomize Participant",
  "randomization.allocation_view": "View Participant Allocation",
  "randomization.arm_view": "View Treatment Arm",
  "randomization.unblind": "Emergency Unblind",
  "randomization.reverse": "Reverse Allocation",
  "randomization.mrt_manage": "Manage MRT Decision Points",
  "randomization.mrt_view": "View MRT Occurrences",
  "randomization.export": "Export Randomization Data",
  "randomization.testbench": "Use Randomization Test Bench",
  "randomization.seed_view": "View Randomization Seed",
  "randomization.stratum_view": "View Stratum Labels",
  "randomization.reason_view": "View Reversal and Unblinding Reasons",
  "randomization.unblinding_view": "View Unblinding Log",
  "randomization.unblinding_disclosure_view": "View Disclosed Arms",

  // Stratification factors. Labels mirror
  // database/seeders/20260912120001-permission.stratification-factors.create.js
  "randomization.config_manage": "Manage Randomization Configuration",
  "randomization.config_withdraw": "Withdraw Randomization Configuration",
  "randomization.site_stratum_add": "Add Site Stratum",
  "query.manage": "Manage Queries",
  "participant.send_survey": "Send Surveys",
  "participant.edit_survey_response": "Edit Survey Response",
  "participant.email": "Send Email to Participant",
  "participant.phone": "View/Edit Participant Phone",
  "participant.download_sync_data": "Download sync data",
};
